export type UserProfile = {
  givenName?: string;
  surname?: string;
  userPrincipalName?: string;
  id?: string;
};

export type Search = {
  showWorkspace: boolean;
  brand?: string;
  division?: string;
  section?: string;
  customerGroup?: string;
  departments?: string[];
};

export const AppMenuId = {
  AssortmentCanvas: 'Ass_canvas',
  AssortmentOverview: 'Ass_Overview',
  ProductPlan: 'pplan',
  AssortmentSupportLearning: 'assortment-support-learning',
} as const;

export type AppMenuIdValue = (typeof AppMenuId)[keyof typeof AppMenuId];

export const ORGANIZATION_DIVISION_API = '/api/Organization/divisions';
export const ORGANIZATION_CORPORATEBRAND_API = '/api/Organization/corporate-brands';
export const ORGANIZATION_CUSTOMERGROUP_API = '/api/Organization/customergroups';
export const ORGANIZATION_SECTION_API = '/api/Organization/sections';
export const ORGANIZATION_DEPARTMENT_API = '/api/Organization/departments';
export const GRAPH_API_URI = 'https://graph.microsoft.com/v1.0/me';

// Selection bar related APIs
export const MARKET_API = '/api/Market/market/';
export const METADATA_DEPARTMENT_API = '/api/Metadata/department/';
export const METADATA_SECTION_API = '/api/Metadata/section/';

// Channel types
export const ChannelType = {
  Store: 1,
  Online: 2,
} as const;
export type ChannelType = (typeof ChannelType)[keyof typeof ChannelType];

// Market model
export const MarketType = {
  PlanningMarket: 1,
  PlanningRegion: 2,
} as const;
export type MarketType = (typeof MarketType)[keyof typeof MarketType];

export type Market = {
  marketCode: number;
  marketType: MarketType;
  marketShortName: string;
  channelIntegrationKey: number;
  corporateBrandIntegrationKey: number;
  isMainMarket: boolean;
  sortOrder: number;
  startSeasonNumber: string;
  endSeasonNumber: string;
  isPlanningMarketMovedToRegion: boolean;
  mainPlanningMarketCode: number;
  mainPlanningMarketShortName: string;
};

// Department metadata
export type DepartmentMetadata = {
  stateGuid: string;
  channelId: number;
  departmentId: number;
  isCoreExtendedChecked: boolean;
  isExcitementChecked: boolean;
  includeExcitementInSummary: boolean;
  selectedMarkets: Market[];
  visibleMarkets: Market[];
};

export type SectionMetadata = DepartmentMetadata & {
  sectionId: number;
  isPlannedArticlesChecked: boolean;
};

// Department metadata response from API
export type DepartmentMetadataResponse = {
  departmentId: number;
  isCoreExtendedChecked: boolean;
  isExcitementChecked: boolean;
  includeExcitementInSummary: boolean;
  selectedMarkets: Market[];
  visibleMarkets: Market[];
  selectedStoreMarkets: Market[];
  selectedOnlineMarkets: Market[];
  stateGuid: string;
  isSuccess: boolean;
  isStateChanged: boolean;
};

export type SectionMetadataModel = DepartmentMetadataResponse & {
  departmentNumber: string;
  isPlannedArticlesChecked: boolean;
  savedBy: string;
  savedTimeUtc: string;
  publishedBy: string;
  publishedTimeUtc: string;
};

export type SectionMetadataResponse = {
  sectionId: number;
  metadata: SectionMetadataModel[];
};

// ── Workspace / grid related models ──────────────────────────────

export type WorkspaceData = {
  isStateChanged: boolean;
  stateGuid: string;
  departmentLevelKpiPlanModels: DepartmentLevelKpiPlanModels[];
};

export type DepartmentLevelKpiPlanModels = {
  id: string;
  kpi: string;
  isCalculated: boolean;
  timeLevel: string;
  planLevel: string;
  isLatestSeason: boolean;
  olderSeasons: string[];
  data: DepartmentLevelKpiDataPoint[];
};

export type DepartmentLevelKpiDataPoint = {
  id: string;
  weekWithYear: string;
  value: number;
  isLatestSeason: boolean;
};

// ── Calculator config ────────────────────────────────────────────
export type CalculatorConfig = {
  kpiKey: string;
  name?: string;
  level: 'W' | 'P' | 'Q';
  dependentKpiKeys: string[];
  formula: (data: Array<number[]>) => number;
  marketCode?: number;
};

// ── KPI selection ────────────────────────────────────────────────
export type KpiSelection = {
  isCoreExtendedChecked: boolean;
  isExcitementChecked: boolean;
  isPlannedArticlesChecked: boolean;
};

// ── SCA (Selection-bar Checkbox Actions) ─────────────────────────
export const ScaActionType = {
  MoveToPlannedArticle: 0,
  MoveToCore: 1,
  DeleteExcitement: 2,
  ExcitementChecked: 3,
  DeletePlannedArticle: 4,
  DeleteAll: 5,
  IncludeExcitementInSummary: 6,
} as const;
export type ScaActionType = (typeof ScaActionType)[keyof typeof ScaActionType];

export type HandleScaSelectionRequest = {
  channel: ChannelType;
  scaActionType: ScaActionType;
  departmentMetadata: DepartmentMetadata;
};

export type DeleteNonMainMarketRequest = {
  channelId: ChannelType;
  nonMainMarketid: number;
  departmentMetadata?: DepartmentMetadata;
};

export type CopyMainMarketRequest = {
  channelId: ChannelType;
  mainMarketid: number;
  nonMainMarketid: number[];
  departmentMetadata?: DepartmentMetadata;
};

export type CopyArticlePlanToAllRegionsRequest = {
  channelId: number;
  mainMarketId: number;
  departmentId: number;
  stateGuid?: string;
  data: {
    id: string;
    weekWithYear?: string;
    value: number;
    isLatestSeason: boolean;
  }[];
};

// ── Timeline models ──────────────────────────────────────────────
export type TimelinePeriod = {
  name?: string;
  weeks: Map<string, string>;
};

export type TimelineQuarter = {
  name?: string;
  periods: Map<string, TimelinePeriod>;
};

export type TimelineMap = {
  currentWeek?: string;
  quarters: Map<string, TimelineQuarter>;
};

// ── Save workspace models ────────────────────────────────────────
export type SaveRequest = {
  channelId: number;
  stateGuid: string;
  data: {
    id: string;
    weekWithYear?: string;
    value: number;
    isLatestSeason: boolean;
  }[];
};

export type SaveResponse = {
  isStateChanged: boolean;
  isSuccess: boolean;
  newStateGuid: string;
};

// ── Publish metadata types ────────────────────────────────────────
export type PublishMetadataPerDepartmentChannel = {
  stateGuid: string;
  departmentId: number;
  publishedTimeUtc: string;
  publishedBy: string;
  channelId: ChannelType;
  isSuccess: boolean;
  isCoreExtendedChecked: boolean;
  isExcitementChecked: boolean;
  selectedMarkets: Market[];
};

export type PublishMetadataPerDepartment = {
  storeMetadataResultModel: PublishMetadataPerDepartmentChannel;
  onlineMetadataResultModel: PublishMetadataPerDepartmentChannel;
};

export type PublishStateModel = {
  storeStateGuid: string;
  storePublishedTimeUtc: string;
  storePublishedBy: string;
  onlineStateGuid: string;
  onlinePublishedTimeUtc: string;
  onlinePublishedBy: string;
};
